import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import type { ArtistRelativeScore, NodeDailyHeat } from '../../../../types/ranking';

interface HotSectionProps {
  category: 'artists' | 'events' | 'nodes' | 'market';
}

interface HotItem {
  id: string;
  name: string;
  image?: string;
  score: number;
  taps?: number;
  rank: number;
  genre?: string;
  location?: string;
  price?: number;
}

export function HotSection({ category }: HotSectionProps) {
  const { profile } = useAuth();

  const [artists, setArtists] = useState<ArtistRelativeScore[]>([]);
  const [nodes, setNodes] = useState<NodeDailyHeat[]>([]);
  const [items, setItems] = useState<HotItem[]>([]);
  const [loading, setLoading] = useState(true);

  const artistsScrollRef = useRef<HTMLDivElement>(null);
  const nodesScrollRef = useRef<HTMLDivElement>(null);

  const userPrefecture = profile?.prefecture ?? undefined;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, userPrefecture]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (category === 'artists') {
        let query = supabase
          .from('artist_relative_scores')
          .select('*')
          .order('total_score', { ascending: false })
          .limit(5);
        if (userPrefecture) {
          query = query.eq('prefecture', userPrefecture);
        }
        const { data } = await query;
        setArtists(data || []);

      } else if (category === 'nodes') {
        let query = supabase
          .from('node_daily_heats')
          .select('*')
          .order('crash_count', { ascending: false })
          .limit(5);
        if (userPrefecture) {
          query = query.eq('prefecture', userPrefecture);
        }
        const { data } = await query;
        setNodes(data || []);

      } else if (category === 'events') {
        // 過去24時間のイベント詳細閲覧数を集計
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        const { data: viewData } = await supabase
          .from('item_views')
          .select('item_id')
          .eq('item_type', 'event')
          .gte('created_at', since);

        if (!viewData || viewData.length === 0) {
          setItems([]);
          return;
        }

        // 閲覧数をitem_idごとに集計
        const viewCounts: Record<string, number> = {};
        viewData.forEach((v) => {
          viewCounts[v.item_id] = (viewCounts[v.item_id] || 0) + 1;
        });

        // 上位8件のevent_idを取得
        const topEventIds = Object.entries(viewCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([id]) => id);

        // イベント詳細を取得
        let eventsQuery = supabase
          .from('events')
          .select('id, title, image_url, prefecture, event_main_genres(genre_name)')
          .in('id', topEventIds);

        if (userPrefecture) {
          eventsQuery = eventsQuery.eq('prefecture', userPrefecture);
        }

        const { data: eventsData } = await eventsQuery;

        if (eventsData && eventsData.length > 0) {
          const ranked = eventsData
            .map((event) => ({
              id: event.id,
              name: event.title || 'Unknown Event',
              image: event.image_url || `https://public.readdy.ai/ai/img_res/7b2ca629eb9c31cbed81267ac8943d37.jpg${event.id}&orientation=landscape`,
              score: viewCounts[event.id] || 0,
              taps: viewCounts[event.id] || 0,
              rank: 0,
              genre: (event as any).event_main_genres?.[0]?.genre_name,
              location: event.prefecture,
            }))
            .sort((a, b) => b.score - a.score)
            .map((item, i) => ({ ...item, rank: i + 1 }));
          setItems(ranked);
        } else {
          setItems([]);
        }

      } else if (category === 'market') {
        // 過去24時間の商品詳細閲覧数を集計
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        const { data: viewData } = await supabase
          .from('item_views')
          .select('item_id')
          .eq('item_type', 'merchandise')
          .gte('created_at', since);

        if (!viewData || viewData.length === 0) {
          setItems([]);
          return;
        }

        // 閲覧数をitem_idごとに集計
        const viewCounts: Record<string, number> = {};
        viewData.forEach((v) => {
          viewCounts[v.item_id] = (viewCounts[v.item_id] || 0) + 1;
        });

        // 上位8件のmerch_idを取得
        const topMerchIds = Object.entries(viewCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([id]) => id);

        const { data: merchData } = await supabase
          .from('merchandise')
          .select('id, name, image_url, price')
          .in('id', topMerchIds)
          .eq('approval_status', 'approved');

        if (merchData && merchData.length > 0) {
          const ranked = merchData
            .map((merch) => ({
              id: merch.id,
              name: merch.name || 'Unknown Item',
              image: merch.image_url || `https://readdy.ai/api/search-image?query=music%20merchandise%20product%20minimal%20clean%20background&width=400&height=400&seq=merch-hot-${merch.id}&orientation=squarish`,
              score: viewCounts[merch.id] || 0,
              taps: viewCounts[merch.id] || 0,
              rank: 0,
              price: merch.price,
            }))
            .sort((a, b) => b.score - a.score)
            .map((item, i) => ({ ...item, rank: i + 1 }));
          setItems(ranked);
        } else {
          setItems([]);
        }
      }
    } catch (err) {
      console.error('Error fetching hot items:', err);
    } finally {
      setLoading(false);
    }
  };

  // 閲覧数を記録する
  const recordView = async (itemId: string, itemType: 'event' | 'merchandise') => {
    await supabase.from('item_views').insert({
      item_id: itemId,
      item_type: itemType,
      viewer_id: profile?.id ?? null,
      prefecture: userPrefecture ?? null,
    });
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-amber-400';
    if (rank === 2) return 'text-zinc-300';
    if (rank === 3) return 'text-orange-400';
    return 'text-zinc-500';
  };

  const getRankBg = (rank: number) => {
    if (rank === 1) return 'bg-amber-400/20 border-amber-400/50';
    if (rank === 2) return 'bg-zinc-300/15 border-zinc-300/40';
    if (rank === 3) return 'bg-orange-400/20 border-orange-400/50';
    return 'bg-zinc-800/60 border-zinc-700/40';
  };

  const prefLabel = userPrefecture ?? '全国';

  const LocalRankingHeader = ({ linkTab }: { linkTab?: string }) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/30">
          <i className="ri-map-pin-2-fill text-brand-primary text-xs" />
          <span className="text-brand-primary text-xs font-bold tracking-wider">{prefLabel}</span>
        </div>
        <span className="text-zinc-500 text-xs">のランキング</span>
        <span className="text-zinc-600 text-[10px]">（過去24時間）</span>
      </div>
      {linkTab && (
        <Link
          to="/ranking"
          state={{ tab: linkTab }}
          className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1 whitespace-nowrap"
        >
          全てを見る
          <i className="ri-arrow-right-line" />
        </Link>
      )}
    </div>
  );

  // ---- ローディング ----
  if (loading) {
    if (category === 'artists' || category === 'nodes') {
      return (
        <div>
          <LocalRankingHeader />
          <div className="flex gap-3 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-20 h-20 bg-zinc-800/50 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-zinc-800/50 rounded-2xl h-64 animate-pulse" />
        ))}
      </div>
    );
  }

  // ---- ARTISTS ランキング（横スライド） ----
  if (category === 'artists') {
    if (artists.length === 0) {
      return (
        <div>
          <LocalRankingHeader linkTab="artists" />
          <div className="rounded-2xl p-10 text-center bg-zinc-800/40 border border-zinc-700/50">
            <i className="ri-inbox-line text-4xl text-zinc-600 block mb-3" />
            <p className="text-zinc-500 text-sm">{prefLabel}のランキングデータがありません</p>
          </div>
        </div>
      );
    }
    return (
      <div>
        <LocalRankingHeader linkTab="artists" />
        <div
          ref={artistsScrollRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory', paddingTop: '8px', paddingLeft: '8px' }}
        >
          {artists.map((artist, index) => {
            const rank = index + 1;
            return (
              <Link
                key={artist.artist_id}
                to={`/artist/${artist.artist_id}`}
                className="flex-shrink-0 relative cursor-pointer group"
                style={{ scrollSnapAlign: 'start', marginTop: '4px', marginLeft: '4px' }}
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden border border-zinc-700/60 group-hover:border-zinc-500/80 transition-all duration-200"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                >
                  {artist.avatar_url ? (
                    <img
                      src={artist.avatar_url}
                      alt={artist.artist_name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <i className="ri-user-star-line text-zinc-500 text-2xl" />
                    </div>
                  )}
                </div>
                <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border flex items-center justify-center z-10 ${getRankBg(rank)}`}
                  style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}
                >
                  <span className={`text-[10px] font-black leading-none ${getRankColor(rank)}`}>{rank}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- NODES ランキング（横スライド） ----
  if (category === 'nodes') {
    if (nodes.length === 0) {
      return (
        <div>
          <LocalRankingHeader linkTab="nodes" />
          <div className="rounded-2xl p-10 text-center bg-zinc-800/40 border border-zinc-700/50">
            <i className="ri-inbox-line text-4xl text-zinc-600 block mb-3" />
            <p className="text-zinc-500 text-sm">{prefLabel}のランキングデータがありません</p>
          </div>
        </div>
      );
    }
    return (
      <div>
        <LocalRankingHeader linkTab="nodes" />
        <div
          ref={nodesScrollRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory', paddingTop: '8px', paddingLeft: '8px' }}
        >
          {nodes.map((node, index) => {
            const rank = index + 1;
            return (
              <Link
                key={node.node_id}
                to={`/shop/${node.node_id}`}
                className="flex-shrink-0 relative cursor-pointer group"
                style={{ scrollSnapAlign: 'start', marginTop: '4px', marginLeft: '4px' }}
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden border border-zinc-700/60 group-hover:border-zinc-500/80 transition-all duration-200 bg-zinc-800"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                >
                  {(node as any).shop_image ? (
                    <img
                      src={(node as any).shop_image}
                      alt={node.node_name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <i className="ri-store-3-line text-zinc-500 text-2xl" />
                    </div>
                  )}
                </div>
                <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border flex items-center justify-center z-10 ${getRankBg(rank)}`}
                  style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}
                >
                  <span className={`text-[10px] font-black leading-none ${getRankColor(rank)}`}>{rank}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- EVENTS / MARKET（カードグリッド） ----
  if (items.length === 0) {
    return (
      <div>
        <LocalRankingHeader />
        <div className="rounded-2xl p-10 text-center bg-zinc-800/40 border border-zinc-700/50">
          <i className="ri-inbox-line text-4xl text-zinc-600 block mb-3" />
          <p className="text-zinc-500 text-sm">過去24時間の閲覧データがありません</p>
        </div>
      </div>
    );
  }

  const getCategoryPath = (itemId: string) => {
    if (category === 'events') return `/event/${itemId}`;
    return `/merchandise`;
  };

  return (
    <div>
      <LocalRankingHeader />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((item) => (
          <Link
            key={item.id}
            to={getCategoryPath(item.id)}
            onClick={() => recordView(item.id, category === 'events' ? 'event' : 'merchandise')}
            className="group rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer relative bg-zinc-800/40 border border-zinc-700/50 hover:bg-zinc-700/50 hover:border-zinc-600/60"
            style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}
          >
            <div className="absolute top-3 left-3 z-10 w-7 h-7 rounded-full flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm border border-zinc-700">
              <span className={`text-xs font-black ${getRankColor(item.rank)}`}>{item.rank}</span>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-3">
              <h4 className="text-sm font-bold text-zinc-100 truncate group-hover:text-cyan-300 transition-colors mb-1">
                {item.name}
              </h4>
              {item.genre && <p className="text-[11px] text-zinc-500 truncate mb-1">{item.genre}</p>}
              {item.location && (
                <p className="text-[11px] text-zinc-600 truncate mb-1">
                  <i className="ri-map-pin-line mr-1" />{item.location}
                </p>
              )}
              {item.price !== undefined && (
                <p className="text-xs text-cyan-300 font-bold mb-1">¥{item.price.toLocaleString()}</p>
              )}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-700/50">
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <i className="ri-eye-line text-zinc-500" />
                  <span>{item.score.toLocaleString()}</span>
                  <span className="text-zinc-600 text-[10px]">回</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
